type WaiterEntry = {
  wake: () => void;
  cancel: () => void;
  timer?: ReturnType<typeof setTimeout>;
};

/**
 * Event-driven wait queue for SW execution gates.
 *
 * ## Why this exists
 *
 * `RateLimitManager` and `RequestSuspender` both need the same primitive:
 * “block callers until something opens the gate, then let them continue”.
 * Previously each class polled with `setTimeout(50)` in a loop. This module
 * replaces that with a waiter list woken by {@link notifyAll} (no polling).
 *
 * ## How it fits in a request
 *
 * ```
 * pull()
 *   → rateLimitManager.waitUntilClear()   // GateWaitQueue waits for 429 cooldown
 *   → requestSuspender.registerWhenReady() // GateWaitQueue waits for resume()
 *   → fetch(...)
 * ```
 *
 * ## API
 *
 * - {@link waitUntilOpen} — wait until `isOpen()` returns true (boolean result)
 * - {@link waitForCallback} — wait until `tryExecute()` returns non-null
 *   (used by `registerWhenReady` to register a request slot in the same sync
 *   turn as `resume()` → `notifyAll()`)
 * - {@link notifyAll} — wake all waiters (call from `resume()` or when a
 *   rate-limit timer fires)
 * - {@link clear} — reject pending waiters with false/null (e.g. on reset)
 *
 * Gate *policy* (when to close/open) lives in RateLimitManager / RequestSuspender.
 * This file is only the *waiting mechanism*.
 */
export class GateWaitQueue {
  private waiters: WaiterEntry[] = [];

  notifyAll(): void {
    // Waiters pushed during this batch are not woken until a later notifyAll.
    const batch = this.waiters.splice(0);
    batch.forEach(({ wake, timer }) => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      wake();
    });
  }

  clear(): void {
    const batch = this.waiters.splice(0);
    batch.forEach(({ cancel, timer }) => {
      if (timer !== undefined) {
        clearTimeout(timer);
      }
      cancel();
    });
  }

  waitUntilOpen(isOpen: () => boolean, timeoutMs?: number): Promise<boolean> {
    if (isOpen()) {
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      let settled = false;

      const finish = (result: boolean, entry: WaiterEntry): void => {
        if (settled) {
          return;
        }
        settled = true;
        this.removeWaiter(entry);
        resolve(result);
      };

      const entry: WaiterEntry = {
        wake: () => {
          if (isOpen()) {
            finish(true, entry);
          }
        },
        cancel: () => finish(false, entry),
      };

      if (timeoutMs !== undefined) {
        entry.timer = setTimeout(() => finish(false, entry), timeoutMs);
      }

      this.waiters.push(entry);
    });
  }

  /**
   * Waits until tryExecute returns non-null/undefined, or timeout.
   * tryExecute is re-run synchronously on each wake (e.g. resume).
   */
  waitForCallback<T>(
    tryExecute: () => T | null | undefined,
    timeoutMs?: number
  ): Promise<T | null> {
    const immediate = tryExecute();
    if (immediate != null) {
      return Promise.resolve(immediate);
    }

    return new Promise((resolve) => {
      let settled = false;

      const finish = (result: T | null, entry: WaiterEntry): void => {
        if (settled) {
          return;
        }
        settled = true;
        this.removeWaiter(entry);
        resolve(result);
      };

      const entry: WaiterEntry = {
        wake: () => {
          const result = tryExecute();
          if (result != null) {
            finish(result, entry);
          }
        },
        cancel: () => finish(null, entry),
      };

      if (timeoutMs !== undefined) {
        entry.timer = setTimeout(() => finish(null, entry), timeoutMs);
      }

      this.waiters.push(entry);
    });
  }

  private removeWaiter(entry: WaiterEntry): void {
    const index = this.waiters.indexOf(entry);
    if (index >= 0) {
      this.waiters.splice(index, 1);
    }
    if (entry.timer !== undefined) {
      clearTimeout(entry.timer);
      entry.timer = undefined;
    }
  }
}
